import { useState } from 'react';
import { DragStart, DropResult, ResponderProvided } from 'react-beautiful-dnd';
import { ContentKeywordGq } from '../../../../../graphql/projects/models/ContentKeywordGq';
import { projectsApiService } from '../../../../../graphql/projects/projectsApiService';
import { ContentKeywordSearchSelectBag } from '../../Tables/ContentKeywordAddTable/logic/useContentKeywordSearchSelectBag';
import { ContentKeywordSearchTableState } from '../../Tables/ContentKeywordAddTable/logic/useContentKeywordSearchTableState';

interface DragLogicInput {
  projectId: number;
  contentTaskId: number;
  addTableState: ContentKeywordSearchTableState;
  addTableSelectBag: ContentKeywordSearchSelectBag;
  reload: () => Promise<unknown>;
  localTaskKeywords: ContentKeywordGq[];
  setLocalTaskKeywords: React.Dispatch<React.SetStateAction<ContentKeywordGq[]>>;
  localAddKeywords: ContentKeywordGq[];
  setLocalAddKeywords: React.Dispatch<React.SetStateAction<ContentKeywordGq[]>>;
}

export function useDragKeywordLogic({
  projectId,
  contentTaskId,
  addTableSelectBag,
  addTableState,
  reload,
  localTaskKeywords,
  setLocalTaskKeywords,
  localAddKeywords,
  setLocalAddKeywords,
}: DragLogicInput) {
  const changeKeywordOrderMutation =
    projectsApiService.mutations.contentPlan.useChangeKeywordOrder();
  const addMutation = projectsApiService.mutations.contentPlan.useAddTaskKeywords();

  const [updating, setUpdating] = useState(false);
  const [isDraggingGlobal, setIsDraggingGlobal] = useState(false);

  // Drag logic
  const handleDragStart = (initial: DragStart) => {
    setIsDraggingGlobal(true);
  };

  // Dropping logic
  const handleDragEnd = (result: DropResult, provided?: ResponderProvided) => {
    setIsDraggingGlobal(false);

    // Irrelevant cases
    if (
      !result.destination ||
      result.destination.droppableId === 'addTable' ||
      (result.destination.index === result.source.index &&
        result.destination.droppableId === result.source.droppableId)
    ) {
      return;
    }

    // Drop case 1: Change order
    if (
      result.source.droppableId === 'mainTable' &&
      (result.destination.droppableId === result.source.droppableId ||
        result.destination.droppableId === 'whiteEmptySpace')
    ) {
      setUpdating(true);
      let newLocalTaskKeywords = [...localTaskKeywords];
      const draggedKeyword = newLocalTaskKeywords[result.source.index];

      // Update item position in array
      newLocalTaskKeywords.splice(result.source.index, 1); //Remove from old position
      //Add to new position
      if (result.destination.droppableId === 'whiteEmptySpace') {
        newLocalTaskKeywords.splice(localTaskKeywords.length + 1, 0, draggedKeyword);
      } else {
        newLocalTaskKeywords.splice(result.destination.index, 0, draggedKeyword);
      }

      // Set local task keywords to immediately reflect the change in the UI (even though it hasen't been added on the backend yet)
      setLocalTaskKeywords(newLocalTaskKeywords);

      // Perform mutation
      changeKeywordOrderMutation({
        projectId,
        contentTaskId: contentTaskId,
        contentKeywordIds: newLocalTaskKeywords.map((i) => i.id),
      })
        .then(reload)
        .finally(() => setUpdating(false));

      // Clean out drag state
      addTableState.setSelectionState((prev) => ({ ...prev, contentKeywordIds: {} }));
      addTableSelectBag.deactivateDragMode && addTableSelectBag.deactivateDragMode();
      return;
    }

    // Drop case 2: Add
    if (result.source.droppableId === 'addTable') {
      // This is a bit complicated because we work with two utilities - drag-n-drop and selection
      // Results
      setUpdating(true);
      let sourceContentKeywords: ContentKeywordGq[] = [];
      let destinationOrderKeywords: ContentKeywordGq[] = [];

      // Concerned keywords
      const draggedAddKeyword = localAddKeywords[result.source.index];
      const selectedAddKeywordIds = addTableState.selectionState.contentKeywordIds;
      const selectedAddKeywords = localAddKeywords.filter((k) =>
        addTableState.backendSelectionState.contentKeywordIds.includes(k.id)
      );

      // Case 2.1 - dragged keyword is NOT in selection, so we move just the dragged keyword
      if (!selectedAddKeywordIds[draggedAddKeyword.id]) {
        // Update local add table state
        const newLocalAddKeywords = [...localAddKeywords];
        //newLocalAddKeywords.splice(result.source.index, 1);
        setLocalAddKeywords(newLocalAddKeywords);

        // Update local main table state
        const newLocalTaskKeywords = [...localTaskKeywords];
        if (result.destination.droppableId === 'whiteEmptySpace') {
          newLocalTaskKeywords.splice(localTaskKeywords.length + 1, 0, draggedAddKeyword);
        } else {
          newLocalTaskKeywords.splice(result.destination.index, 0, draggedAddKeyword);
        }
        setLocalTaskKeywords(newLocalTaskKeywords);

        // Prepare mutation data
        sourceContentKeywords = [draggedAddKeyword];
        destinationOrderKeywords = newLocalTaskKeywords;
      }

      // Case 2.2 - dragged keyword is in selection, so we move all of selection
      else if (selectedAddKeywordIds[draggedAddKeyword.id]) {
        // Update local add table state
        const newLocalAddKeywords = [...localAddKeywords];
        newLocalAddKeywords.splice(result.source.index, selectedAddKeywords.length);
        setLocalAddKeywords(newLocalAddKeywords);

        // Update local main table state
        const newLocalTaskKeywords = [...localTaskKeywords];
        if (result.destination.droppableId === 'whiteEmptySpace') {
          newLocalTaskKeywords.splice(localTaskKeywords.length + 1, 0, ...selectedAddKeywords);
        } else {
          newLocalTaskKeywords.splice(result.destination.index, 0, ...selectedAddKeywords);
        }
        setLocalTaskKeywords(newLocalTaskKeywords);

        // Prepare mutation data
        sourceContentKeywords = selectedAddKeywords;
        destinationOrderKeywords = newLocalTaskKeywords;
      }

      // Clean out drag state
      addTableState.setSelectionState((prev) => ({ ...prev, contentKeywordIds: {} }));
      addTableSelectBag.deactivateDragMode && addTableSelectBag.deactivateDragMode();

      // Perform mutation
      addMutation({
        projectId,
        sourceContentKeywordIds: sourceContentKeywords.map((i) => i.id),
        destinationContentTaskId: contentTaskId,
        destinationOrderKeywordIds: destinationOrderKeywords.map((i) => i.id),
      })
        .then(() => Promise.all([reload()]))
        .finally(() => setUpdating(false));
    }
  };

  return {
    updatingDragKeywords: updating,
    isDraggingGlobal,
    handleDragEnd,
    handleDragStart,
  };
}
